import { currentWallet } from '@cronos-app/wallet';
import { useQuery } from '@tanstack/react-query';
import { Fraction } from 'bi-fraction';
import { useMemo } from 'react';

import type { ExtendedTokenInfo } from '../types';
import { useCardLayer } from '../useCardContextStore';
import { getCdcDepositCardService } from './useCdcDepositCardService';
import { useSwapAndTopUpInfo } from './useSwapAndTopUpInfo';

export const useEstimateTopUpGas = (
  from: ExtendedTokenInfo,
  amount: string,
) => {
  const network = currentWallet.useDesiredNetwork();
  const account = currentWallet.useAccount();
  const layer = useCardLayer();
  const provider = currentWallet.useProvider();
  const { data } = useSwapAndTopUpInfo(from, amount);
  const { data: gas } = useQuery({
    queryKey: ['useEstimateTopUpGas', network, data, account, provider, layer],
    queryFn: async () => {
      if (!data || !account || !provider)
        throw new Error('Data is not available');
      return getCdcDepositCardService(network, layer).estimateGas_swapAndTopUp(
        account,
        data,
        await provider.getSigner(),
        true,
      );
    },
    enabled: !!data && !!account && !!provider,
  });
  const { data: gasPrice } = useQuery({
    queryKey: ['gasPrice', provider],
    queryFn: async () => {
      if (!provider) throw new Error('Provider is not connected');
      const gasPrice = (await provider.getFeeData()).gasPrice;
      if (!gasPrice) throw new Error('Gas price not available');
      return new Fraction(gasPrice).shr(18);
    },
    enabled: !!provider,
  });
  return useMemo(() => {
    if (!gas || !gasPrice) return '';
    return new Fraction(gas)
      .mul(gasPrice)
      .add(data?.needToSwapFirst ? data.gasEstimate : '0')
      .toFixed(18);
  }, [data, gas, gasPrice]);
};
